import React from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../../components/SeoHead'
import { COMPANY, BELAEGNING_FAQ, IMAGES } from '../../data/siteData'
import { PAGE_META } from '../../data/metaData'
import { getOptimizedImageUrl } from '../../lib/imageHelper'
import ContactForm from '../../components/ContactForm'
import GallerySection from '../../components/GallerySection'
import FaqAccordion from '../../components/FaqAccordion'

export default function BelaegningPage() {
  const metaTitle = PAGE_META.belaegning.title
  const metaDescription = PAGE_META.belaegning.description

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: BELAEGNING_FAQ.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        schemaData={faqSchema}
        canonicalPath="/belaegning"
      />

      {/* Hero */}
      <section className="relative bg-stone-950 text-white overflow-hidden py-16 sm:py-24 border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(IMAGES.belaegning.hero, { width: 1800, quality: 80 })}
            alt="Flisebelægning foran moderne rødt murstenshus med grå havefliser"
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/70 via-stone-950/35 to-stone-950/15"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs sm:text-sm font-semibold mb-4">
              <i className="fa-solid fa-shapes" aria-hidden="true"></i>
              <span>Terrasser, indkørsler & havegange</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
              Belægning der gør haven smukkere og nemmere at bruge
            </h1>

            <p className="mt-5 text-base sm:text-xl text-stone-300 leading-relaxed max-w-2xl">
              Drømmer du om en terrasse, en havesti eller et indgangsparti, der passer naturligt til boligen? Hos <Link to="/" className="text-stone-200 font-semibold underline decoration-amber-500 decoration-2 underline-offset-2 hover:text-white transition-colors">JT Knudsen</Link> hjælper vi med belægning, hvor funktion, udtryk og omgivelser hænger sammen. Du får en løsning, der er gennemtænkt fra underlaget til den færdige overflade.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
              <a
                href="#kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-lg bg-[#015205] hover:bg-[#026b07] text-white font-bold text-base shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Få tilbud på belægning</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </a>

              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-lg bg-stone-900 hover:bg-stone-800 text-stone-100 font-bold text-base border border-stone-700 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-amber-400" aria-hidden="true"></i>
                <span>Ring på {COMPANY.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Hvor skal din have belægning */}
      <section className="py-24 sm:py-32 lg:py-36 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Uderum med sammenhæng
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">
                Hvor skal din have belægning, og hvor skal det grønne fylde?
              </h2>
              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  God belægning skal gøre haven lettere at bruge uden at tage opmærksomheden fra bede, græs og beplantning. Vi ser på havens indretning, boligens stil og de naturlige ganglinjer. På den måde bliver flisearealerne placeret, så de skaber sammenhæng og har en tydelig funktion.
                </p>
                <p>
                  Have belægning kan bruges til at skabe tørre adgangsveje, hyggelige opholdsarealer og tydelige overgange mellem forskellige dele af haven. Vi hjælper dig med at vurdere arealets størrelse og form, så resultatet hverken virker tungt eller tilfældigt, men bliver en naturlig del af uderummet.
                </p>
                <p>
                  Ved større haveprojekter kombineres flisearbejdet ofte med en diskret og sikker <Link to="/nedgravning-af-trampolin" className="text-amber-600 hover:text-amber-700 font-semibold underline underline-offset-2">nedgravning af trampolin</Link>, så haverummet bevarer sit rolige og åbne udtryk. Skal der i forbindelse med terrasse eller indkørsel også opføres et skur, en carport eller en tilbygning, sørger vi desuden for grundig udgravning og støbning af <Link to="/fundament" className="text-amber-600 hover:text-amber-700 font-semibold underline underline-offset-2">fundament og sokkel</Link>.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-200">
                <img
                  src={getOptimizedImageUrl(IMAGES.belaegning.driveway, { width: 800, height: 600 })}
                  alt="Nyanlagt indkørsel og havesti med belægningssten og stabilt bærelag"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Hvad sker der under fliserne? */}
      <section className="py-16 sm:py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
              Korrekt opbygning
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1">
              Hvad sker der under fliserne?
            </h2>
            <div className="mt-4 text-stone-300 space-y-3 text-base sm:text-lg leading-relaxed">
              <p>
                Det meste af det afgørende arbejde bliver skjult, når belægningen er færdig. Underlaget skal graves ud i den rette dybde og bygges op, så fliserne ligger stabilt. Bærelag, afretningslag og komprimering har stor betydning for, om overfladen forbliver jævn og modstår den daglige belastning.
              </p>
              <p>
                Vi tager også højde for fald og regnvand, så vandet ledes hensigtsmæssigt væk fra bygninger og opholdsarealer. Den præcise opbygning afhænger af jordbund, anvendelse og flisetype. En terrasse har eksempelvis ikke samme belastning som en indkørsel og skal derfor vurderes ud fra andre behov.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-lg mb-4">
                <i className="fa-solid fa-trowel" aria-hidden="true"></i>
              </div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Præcis udgravning
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Udgravning til den valgte opbygning og underlagets bæreevne.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-lg mb-4">
                <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
              </div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Stabilt bærelag
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Stabilt bærelag og jævnt afretningslag komprimeret grundigt.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-lg mb-4">
                <i className="fa-solid fa-droplet-slash" aria-hidden="true"></i>
              </div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Korrekt afvanding
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Fald der leder regnvand hensigtsmæssigt væk fra murværk og sokkel.
              </p>
            </div>

            <div className="bg-stone-950/70 border border-stone-800 rounded-xl p-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-lg mb-4">
                <i className="fa-solid fa-border-all" aria-hidden="true"></i>
              </div>
              <h4 className="font-bold text-base text-stone-100 mb-2">
                Stærke afslutninger
              </h4>
              <p className="text-xs sm:text-sm text-stone-400">
                Kanter og fuger der holder belægningen fast og samlet år efter år.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Hvilken belægning til terrasse */}
      <section className="py-24 sm:py-32 lg:py-36 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-200">
                <img
                  src={getOptimizedImageUrl(IMAGES.belaegning.detail, { width: 800, height: 600 })}
                  alt="Håndværksmæssig lægning og præcis afretning af fliser på sandlag"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-6 space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Design & Materialevalg
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">
                Hvilken belægning til terrasse passer bedst til dit hjem?
              </h2>
              <div className="text-stone-700 space-y-3 text-base leading-relaxed">
                <p>
                  Terrassen er ofte det sted, hvor udelivet samles omkring måltider, afslapning og tid med familie og venner. Den rette belægning til terrasse skal derfor være behagelig at færdes på og passe til den måde, arealet skal bruges. Samtidig bør farve og format spille naturligt sammen med huset.
                </p>
                <p>
                  Store fliser kan give et roligt og enkelt udtryk, mens mindre belægningssten giver mulighed for flere mønstre og detaljer. Vi rådgiver ud fra arealets form, møblering og adgangsveje. Målet er en terrasse, der føles rummelig, fungerer i hverdagen og bliver en integreret del af haven.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Balance mellem udtryk og funktion & Pris */}
      <section className="py-16 sm:py-20 bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Praktiske og visuelle hensyn
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Sådan finder vi balancen mellem udtryk og funktion
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Flisernes udseende er vigtigt, men valget bør også tage højde for overflade, rengøring og løbende vedligeholdelse. Vi hjælper med at samle de praktiske og visuelle hensyn, så du får belægning, der passer til dine ønsker og til de forhold, som findes omkring boligen.
              </p>
              <ul className="space-y-2.5 text-sm sm:text-base text-stone-700 pt-2 border-t border-stone-100">
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-600" aria-hidden="true"></i>
                  <span>Arealets størrelse og ønskede anvendelse</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-600" aria-hidden="true"></i>
                  <span>Flisernes format, farve og overflade</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-600" aria-hidden="true"></i>
                  <span>Sammenhængen med hus, bede og adgangsveje</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-amber-600" aria-hidden="true"></i>
                  <span>Behovet for rengøring og løbende vedligeholdelse</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-7 sm:p-9 rounded-2xl border border-stone-200 shadow-sm space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Budget & Omfang
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Hvad koster ny belægning?
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Prisen på belægning afhænger blandt andet af arealets størrelse, adgangsforholdene og det eksisterende underlag. Valget af fliser, behovet for udgravning og eventuel bortkørsel af jord påvirker også det samlede arbejde. Derfor tager vi udgangspunkt i dit konkrete projekt frem for en fast standardpris.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                En grundig gennemgang gør det lettere at vælge en løsning, der passer til både ønsker og budget. Her kan vi afklare opbygning, materialer, afslutninger og områdets anvendelse. Det giver et bedre beslutningsgrundlag og mindsker risikoen for uventede forhold, når arbejdet går i gang.
              </p>
              <div className="pt-2">
                <a
                  href="#kontakt"
                  className="inline-flex items-center gap-2 text-sm font-bold text-amber-600 hover:text-amber-700"
                >
                  <span>Kontakt Jonas for en fast aftale om pris</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local coverage snippet (same design as section 84: 2-col 7/5 with rounded-2xl shadow-lg border card) */}
      <section className="py-24 sm:py-32 lg:py-36 bg-stone-50 text-stone-900 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Lokal tilstedeværelse
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-stone-900">
                Belægning i Østjylland og på Djursland med lokal tilstedeværelse
              </h2>
              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  JT Knudsen har base i Hjortshøj og kører i Østjylland samt på Djursland. Vi hjælper blandt andet med belægning i Hjortshøj, Aarhus, Egå, Hinnerup, Hornslet og Grenaa. Den lokale placering gør det nemt at tage en direkte dialog om både mindre arealer og større belægningsopgaver.
                </p>
                <p>
                  Du får en personlig kontakt og en faglig vurdering af mulighederne på din grund. Vi lægger vægt på klare aftaler og et resultat, der fungerer i praksis. Uanset om du ønsker en terrasse, en havesti eller et nyt opholdsareal, tager løsningen udgangspunkt i boligen og dine behov.
                </p>
              </div>

              <div className="pt-3">
                <a
                  href="#kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-stone-900 hover:bg-stone-800 text-stone-100 text-base font-bold transition-colors cursor-pointer"
                >
                  <span>Få en vurdering af din opgave</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-200">
                <img
                  src={getOptimizedImageUrl(IMAGES.belaegning.hero, { width: 800, height: 600 })}
                  alt="Færdigt flisearbejde og belægning i forhave ved murstenshus"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery for Belægning */}
      <section className="py-16 bg-stone-100/70 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GallerySection
            galleryKey="belaegning"
            title="Billeder af belægningsarbejde"
            subtitle="Se eksempler på flisebelægning, terrasser, gangarealer og indkørsler"
          />
        </div>
      </section>

      {/* FAQ Accordion */}
      <FaqAccordion
        items={BELAEGNING_FAQ}
        title="Spørgsmål om have- og terrassebelægning"
        subtitle="Svar på valg af fliser, underlagets opbygning, fald og vedligeholdelse"
      />

      {/* Main Contact Section */}
      <section className="py-16 sm:py-24 bg-stone-950 text-white border-t border-stone-800" id="kontakt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                <span>Uforpligtende tilbud</span>
              </span>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Klar til et uderum, du får lyst til at bruge?
              </h2>

              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Fortæl os, hvad du forestiller dig, og hvordan området skal bruges. Så kan vi tage en snak om udformning, underlag og valg af belægning. Kontakt JT Knudsen for at få vurderet dit projekt og høre mere om mulighederne for en flot og funktionel løsning ved din bolig.
              </p>

              <div className="p-6 rounded-xl bg-stone-900 border border-stone-800 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-user text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      Fagmand & Indehaver
                    </div>
                    <div className="text-base font-bold text-white">
                      {COMPANY.name}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      Telefon
                    </div>
                    <a
                      href={COMPANY.phoneHref}
                      className="text-lg font-bold text-amber-400 hover:text-amber-300 transition-colors whitespace-nowrap"
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      E-mail
                    </div>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-sm font-semibold text-stone-200 hover:text-amber-400 transition-colors whitespace-nowrap break-keep"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-location-dot text-base" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-400 font-medium uppercase tracking-wider">
                      Adresse & base
                    </div>
                    <div className="text-sm text-stone-300">
                      {COMPANY.address}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm defaultService="Belægning" />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
